import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import { useGetDashboardStatsQuery } from '../../store/api/ticketApi';
import { Loader } from '../../components/ui/Loader';

export function SupportDashboardPage() {
  const { t } = useTranslation();
  const { data, isLoading } = useGetDashboardStatsQuery();
  const stats = data?.data;

  return (
    <div className="min-h-screen relative">
      <div className="container mx-auto px-4 py-16 content-overlay">
        <div className="max-w-6xl mx-auto">
          {/* Header */}
          <div className="mb-8 text-center animate-slide-up">
            <h1 className="text-4xl md:text-5xl font-heading font-bold text-brand-accent mb-4">
              {t('support.dashboard.title')}
            </h1>
            <p className="text-neutral-medium text-lg font-body">
              {t('support.dashboard.subtitle')}
            </p>
          </div>

          {/* Statistics Cards */}
          {isLoading && (
            <div className="mb-8 text-center py-16">
              <Loader />
            </div>
          )}

          {!isLoading && stats && (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8 animate-scale-in">
              {/* Open Tickets */}
              <div className="card-glass rounded-2xl shadow-2xl p-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-heading font-semibold text-neutral-light">
                    {t('support.dashboard.stats.open.title')}
                  </h3>
                  <div className="w-12 h-12 bg-green-500/20 rounded-full flex items-center justify-center">
                    <svg
                      className="w-6 h-6 text-green-500"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M5 13l4 4L19 7"
                      />
                    </svg>
                  </div>
                </div>
                <div className="text-3xl font-heading font-bold text-green-500">
                  {stats.open}
                </div>
              </div>

              {/* Pending Tickets */}
              <div className="card-glass rounded-2xl shadow-2xl p-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-heading font-semibold text-neutral-light">
                    {t('support.dashboard.stats.pending.title')}
                  </h3>
                  <div className="w-12 h-12 bg-yellow-500/20 rounded-full flex items-center justify-center">
                    <svg
                      className="w-6 h-6 text-yellow-500"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                      />
                    </svg>
                  </div>
                </div>
                <div className="text-3xl font-heading font-bold text-yellow-500">
                  {stats.pending}
                </div>
              </div>

              {/* Closed Tickets */}
              <div className="card-glass rounded-2xl shadow-2xl p-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-heading font-semibold text-neutral-light">
                    {t('support.dashboard.stats.closed.title')}
                  </h3>
                  <div className="w-12 h-12 bg-gray-500/20 rounded-full flex items-center justify-center">
                    <svg
                      className="w-6 h-6 text-gray-500"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M6 18L18 6M6 6l12 12"
                      />
                    </svg>
                  </div>
                </div>
                <div className="text-3xl font-heading font-bold text-gray-500">
                  {stats.closed}
                </div>
              </div>

              {/* Total Tickets */}
              <div className="card-glass rounded-2xl shadow-2xl p-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-heading font-semibold text-neutral-light">
                    {t('support.dashboard.stats.total.title')}
                  </h3>
                  <div className="w-12 h-12 bg-brand-accent/20 rounded-full flex items-center justify-center">
                    <svg
                      className="w-6 h-6 text-brand-accent"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M9 12h6m-3-3v6m-9 4h18a2 2 0 002-2V6a2 2 0 00-2-2H3a2 2 0 00-2 2v8a2 2 0 002 2z"
                      />
                    </svg>
                  </div>
                </div>
                <div className="text-3xl font-heading font-bold text-brand-accent">
                  {stats.total}
                </div>
              </div>
            </div>
          )}

          {/* Quick Actions */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 animate-scale-in">
            <Link
              to="/support/tickets"
              className="block card-glass rounded-lg p-6 hover:border-brand-accent/50 hover:shadow-lg hover:shadow-brand-accent/20 transform hover:scale-105 transition-all duration-300 group"
            >
              <div className="flex flex-col items-center text-center">
                <div className="w-16 h-16 bg-brand-accent/20 rounded-full flex items-center justify-center mb-4 group-hover:bg-brand-accent/30 transition-colors">
                  <svg
                    className="w-8 h-8 text-brand-accent"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"
                    />
                  </svg>
                </div>
                <h3 className="text-xl font-heading font-bold text-brand-accent mb-2 group-hover:text-brand-accent/80 transition-colors">
                  {t('support.dashboard.allTickets.title')}
                </h3>
                <p className="text-sm text-neutral-medium font-body">
                  {t('support.dashboard.allTickets.description')}
                </p>
              </div>
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
